/**
 * #1901 (FS16780) — the search / group rules for the Phone Sheet and the Attendance list.
 *
 * Pure functions in one module, the way `safeHtmlRules.js` and `assignBlock.js` are, for two
 * reasons: the two pages must agree about what "matches" means (they are the same question asked
 * of two different row shapes), and rules in a `.vue` file cannot be unit-tested without a DOM.
 * The pinned behaviour is in `tests/phone-sheet-search-1901.test.mjs`.
 *
 * Nothing here formats or renders. Date and phone formatting stay with the components, which
 * already own them (`getTime()` / `getClientContactFormat()`), and are passed in — so a group
 * header reads exactly like the Start column of the rows beneath it.
 */

/** Case-insensitive substring over the given fields. Empty/blank query matches everything. */
export function matchesQuery(parts, query) {
  const q = (query || '').trim().toLowerCase();
  if (q === '') return true;
  return parts
    .map((v) => (v === null || v === undefined ? '' : String(v)))
    .join(' ')
    .toLowerCase()
    .indexOf(q) !== -1;
}

/** The first usable number from a `phones` array, or null. */
export function firstPhoneNumber(phones) {
  const list = Array.isArray(phones) ? phones : [];
  for (const ph of list) {
    if (ph && ph.phone_number) return String(ph.phone_number);
  }
  return null;
}

/** 24-hour `HH:mm` for grouping and ordering — NOT for display. '' when there is no start. */
export function startKey(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2);
}

/**
 * #1901 — a day's rows grouped by start time.
 *
 * Ed: "If there are multiple start times in a day, the day should be collapsible by start time."
 * With ONE start time there is nothing to collapse, so `multi` is false and the caller renders no
 * header — the day keeps exactly the shape it has today.
 *
 * ⚠ Grouped and ORDERED on `startKey` (24-hour `HH:mm`), never on the displayed label: a local
 * 12-hour label sorts "10:00 AM" before "9:00 AM" as a string, which would put the groups in the
 * wrong order. `labelOf` is only ever used for display.
 *
 * @param {Array}    rows
 * @param {string}   dayKey    scopes the returned group keys, so two days cannot collide
 * @param {Function} startOf   row  -> the raw start value
 * @param {Function} labelOf   raw start value -> the string to show
 * @returns {Array<{start:string,label:string,key:string,rows:Array,multi:boolean}>}
 */
export function startTimeGroups(rows, dayKey, startOf, labelOf) {
  const list = Array.isArray(rows) ? rows : [];
  const order = [];
  const byStart = {};
  for (const row of list) {
    const key = startKey(startOf(row));
    if (!(key in byStart)) { byStart[key] = []; order.push(key); }
    byStart[key].push(row);
  }
  order.sort();
  const multi = order.length > 1;
  return order.map((start) => {
    const raw = startOf(byStart[start][0]);
    return {
      start: start,
      label: raw ? labelOf(raw) : '',
      key: dayKey + '|' + start,
      rows: byStart[start],
      multi: multi,
    };
  });
}

/**
 * #1901 — the sort order the Attendance grid's CollectionView must carry.
 *
 * ⚠ THE FIRST DESCRIPTOR DECIDES THE GROUP ORDER, AND THAT IS THE WHOLE POINT.
 * Wijmo's `groupDescriptions` does not order groups: it groups the rows in whatever order the
 * `sortDescriptions` leave them, so the group headers come out in the order their first row
 * happens to appear. `buildView()` sorted only by `employee` (and `arrived_order`), so a day's
 * start-time headers followed NAME order — on job d19f1740 cs3tests measured headers reading
 * `0800, 0600, 0100, 1130` in Chrome and WebKit. Putting `start` first fixes it.
 *
 * `start` on these rows is a zero-padded 24-hour `HHMM` string (see getCallData), so a plain
 * ascending string sort IS chronological — the same reason {@link startTimeGroups} keys on
 * `HH:mm` and never on the displayed 12-hour label, which would sort "10:00 AM" before
 * "9:00 AM". **Always ascending**: Z-A reverses the names WITHIN a start time, never the
 * running order of the day.
 *
 * ⚠ `start` is only included when the day is actually grouped. With one start time there is no
 * group row, and sorting by a column every row shares would just add a no-op comparison.
 *
 * Returned as plain descriptors rather than Wijmo `SortDescription`s so the rule is testable
 * without Wijmo — it needs a DOM to load, and the one jsdom in node_modules is an undeclared
 * transitive dependency that an `npm ci` could remove. The component maps these to
 * `SortDescription`; `phone-sheet-search-1901.test.mjs` asserts the ordering they produce.
 *
 * @param {{grouped?:boolean, sortMode?:string, nameDir?:string}} opts
 * @returns {Array<{property:string, ascending:boolean}>} in priority order
 */
export function attendanceSortDescriptions(opts) {
  const { grouped = false, sortMode = 'name', nameDir = 'asc' } = opts || {};
  const descriptions = [];

  // 1. chronological groups, before anything else
  if (grouped) descriptions.push({ property: 'start', ascending: true });
  // 2. within a group: not-yet-arrived first, when that mode is on
  if (sortMode === 'arrived') descriptions.push({ property: 'arrived_order', ascending: true });
  // 3. then by name, in the chosen direction
  descriptions.push({ property: 'employee', ascending: nameDir !== 'desc' });

  return descriptions;
}
